import {
  Box,
  Button,
  Flex,
  FormInput,
  fromTheme,
  Heading,
  StrokeIcon,
  useIsSticky,
} from '@acsbe/ui';
import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import { StripeError } from '@stripe/stripe-js/dist/stripe-js/stripe';
import axios from 'axios';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  useBilling,
  useDomainSelection,
  useMerchantSession,
} from '../../../context';
import { StripeLoader, useStripeModule } from '../../../context/stripe';
import { useAnalytics } from '../../../services/analytics';
import { PAYMENT_METHOD_REQUESTED } from '../../../services/analytics/event-names';
import { useZodForm } from '../../../utils/forms';
import { StripeNameCardForm } from '../../../validations/billing';

type StripeFormProps = {
  country?: string;
};

const paymentElementOptions: StripePaymentElementOptions = {
  terms: {
    card: 'never',
  },
  layout: {
    type: 'tabs',
  },
} as const;

export const StripeForm: FunctionComponent<StripeFormProps> = ({
  country = 'US',
}) => {
  const [paymentReady, setPaymentReady] = useState(false);
  const [addressReady, setAddressReady] = useState(false);
  const { currentAccessibeAccount } = useDomainSelection();
  const { accessibeUser } = useMerchantSession();
  const { setIsNewPaymentMethodAdded, billingAddress } = useBilling();

  const hasSingleBillingAddress = Boolean(
    currentAccessibeAccount?.singleBillingAddress,
  );

  const showAddressForm = !hasSingleBillingAddress || !billingAddress?.address;
  const ready = paymentReady && (!showAddressForm || addressReady);

  const { PaymentElement, AddressElement, useElements, useStripe } =
    useStripeModule();
  const { hookup, handleSubmit } = useZodForm(StripeNameCardForm);

  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const analytics = useAnalytics();
  const { isSticking, stickyElementRef } =
    useIsSticky<HTMLDivElement>('bottom');
  const [isLoading, setIsLoading] = useState(false);

  const addressRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onSubmit = handleSubmit(
    async ({ nameOnCard }: { nameOnCard: string }) => {
      try {
        setIsLoading(true);
        if (!stripe || !elements) {
          return;
        }

        const { error, setupIntent } = await stripe!.confirmSetup({
          elements,
          redirect: 'if_required',
        });
        if (error) throw error;

        const response = await axios.post(
          '/api/stripe/add-payment-method',
          {
            setupIntentId: setupIntent.id,
            nameOnCard,
          },
          {
            withCredentials: true,
          },
        );

        setIsNewPaymentMethodAdded(true);
      } catch (err: unknown) {
        const stripeError = err as StripeError;

        analytics.mixpanel.trackEvent(PAYMENT_METHOD_REQUESTED, {
          status: 'fail',
          failureReason:
            stripeError.message?.replace(/['‘’]/g, '') ?? 'unknown',
          isReplaced: false,
        });

        if (stripeError.code === 'incomplete_number') {
          paymentRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }

        if (stripeError.code === 'incomplete_address') {
          addressRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      } finally {
        setIsLoading(false);
      }
    },
  );

  const handleFocus = () => {
    timeoutRef.current = setTimeout(() => {
      addressRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 50);
  };

  useEffect(() => {
    if (!elements) return;

    const addressElement = elements.getElement('address');

    if (!addressElement) return;

    addressElement.on('change', handleFocus);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      addressElement.off('change', handleFocus);
    };
  }, [elements]);

  if (!stripe) return null;

  return (
    <Flex padding={6} self="stretch">
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        aria-label="Set up your payment method"
        aria-busy={isLoading}
      >
        <Flex radius="xl" gap="lg">
          {ready ? (
            <StripeLikeInput
              defaultValue={accessibeUser?.fullName}
              label={t('STRIPE.NAME_LABEL')}
              placeholder={t('STRIPE.NAME_PLACEHOLDER')}
              type="text"
              {...hookup('nameOnCard')}
            />
          ) : (
            <StripeLoader />
          )}

          <Box ref={paymentRef}>
            <PaymentElement
              options={paymentElementOptions}
              onReady={() => setPaymentReady(true)}
            />
          </Box>

          {showAddressForm ? (
            <Box>
              {ready && (
                <Box paddingBlock={4}>
                  <Heading emphasis="high" level={3}>
                    {t('STRIPE.BILLING_ADDRESS')}
                  </Heading>
                </Box>
              )}
              <Box ref={addressRef}>
                <AddressElement
                  options={{
                    mode: 'billing',
                    display: {
                      name: 'organization',
                    },
                    defaultValues: {
                      name: currentAccessibeAccount?.name,
                      address: {
                        country: country,
                      },
                    },
                  }}
                  onReady={() => setAddressReady(true)}
                />
              </Box>
            </Box>
          ) : null}
        </Flex>

        {ready && (
          <Footer
            ref={stickyElementRef}
            data-sticky={isSticking}
            position="sticky"
            marginBlockStart={8}
          >
            <Button
              type="submit"
              data-mixpanel="add-payment-method"
              disabled={!stripe || !elements || isLoading}
              scale="primary"
            >
              {t('STRIPE.STRIPE_CONFIRM_CTA')}
              <StrokeIcon name="ChevronRight" />
            </Button>
          </Footer>
        )}
      </form>
    </Flex>
  );
};

const Footer = styled(Flex)`
  inset-block-end: calc(0px - var(--padding));
  width: 200px;

  &[data-sticky='true'] {
    border-top: 1px solid ${fromTheme('palette.scales.base.border.static')};
  }
`;

const StripeLikeInput = styled(FormInput)`
  --xseb-max-input-width: 100%;
  display: flex;

  input {
    flex: 1;
    transition:
      background 0.15s ease,
      border 0.15s ease,
      box-shadow 0.15s ease,
      color 0.15s ease;
  }
`;
